import React, { useEffect, useState, useContext } from "react";
import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Accordion,
  Button,
} from "semantic-ui-react";
import ProjectEditorModal from "./ProjectEditorModal";
import ProjectArchivePanel from "./ProjectArchivePanel";
import _ from "lodash";
import {
  config,
  PROJECT_STATUSES,
  USERTYPES,
} from "../../util/functions/constants";
import ProjectViewerModal from "./ProjectViewerModal";
import WebsiteViewerModal from "./WebsiteViewerModal";
import { isSemesterActive } from "../../util/functions/utils";
import { UserContext } from "../../util/functions/UserContext";
import { canEditProject } from "../../util/functions/projectPermissions";
import "./../../../css/components/proposal.css";
import "./../../../css/containers/accordion.css";
import "./../../../css/components/modal.css";

const COLUMNS = {
  SEMESTER: "semester",
  STATUS: "status",
  TITLE: "title",
};

const ASCENDING = "ascending";
const DESCENDING = "descending";

/**
 * This is what is displayed inside the admin tab for projects. It may also be used elsewhere in the website.
 * */
export default function Proposals(props) {
  const [proposalData, setProposalData] = useState({});
  const [active, setActive] = useState(
    isSemesterActive(props.semester?.start_date, props.semester?.end_date),
  );
  const [projectPermissions, setProjectPermissions] = useState({});
  const userContext = useContext(UserContext);

  let semesterMap = { undefined: "No Semester", null: "No Semester" };
  props.semesterData?.forEach((semester) => {
    semesterMap[semester.semester_id] = semester.name;
  });

  useEffect(() => {
    const newProposalData = {
      proposals: props.proposalData,
      column: COLUMNS.SEMESTER,
    };
    newProposalData.proposals = _.sortBy(
      newProposalData.proposals,
      COLUMNS.SEMESTER,
    ).reverse();
    setProposalData(newProposalData);

    // For All Projects view with user projects list, create simple permissions map
    if (props.userProjects && props.proposalData) {
      const permissions = {};
      const userProjectIds = props.userProjects.map((p) => p.project_id);

      props.proposalData.forEach((proposal) => {
        permissions[proposal.project_id] = userProjectIds.includes(
          proposal.project_id,
        );
      });

      setProjectPermissions(permissions);
    }
  }, [props.proposalData, props.userProjects]);

  /*const changeSort = (column) => {
    if (proposalData.column === column) {
      setProposalData({
        column: column,
        direction:
          proposalData.direction === ASCENDING ? DESCENDING : ASCENDING,
        proposals: proposalData.proposals.slice().reverse(),
      });
      return;
    }

    setProposalData({
      direction: DESCENDING,
      column: column,
      proposals: _.sortBy(proposalData.proposals, [column]),
    });
  };*/

  const renderProposals = () => {
    if (!proposalData.proposals) {
      return (
        <TableRow textAlign="center">
          <TableCell>
            <Icon name="spinner" />
          </TableCell>
        </TableRow>
      );
    }

    if (proposalData.proposals?.length === 0) {
      return (
        <TableRow textAlign="center">
          <TableCell>No projects</TableCell>
        </TableRow>
      );
    }

    return proposalData.proposals.map((proposal, idx) => {
      let rowColor;
      switch (proposal.status) {
        case PROJECT_STATUSES.ARCHIVED:
        case PROJECT_STATUSES.COMPLETE:
          rowColor = "proposal-row-gray";
          break;
        case PROJECT_STATUSES.CANDIDATE:
          rowColor = "proposal-row-green";
          break;
        case PROJECT_STATUSES.IN_PROGRESS:
          rowColor = "proposal-row-yellow";
          break;
        default:
          rowColor = "";
          break;
      }
      return (
        <TableRow className={rowColor} key={idx}>
          <TableCell>{semesterMap[proposal.semester]}</TableCell>
          <TableCell>{proposal.display_name || proposal.title}</TableCell>
          <TableCell>{proposal.status}</TableCell>
          <TableCell>
            <div className="accordion-buttons-container">
              {props.viewOnly ? (
                <>
                  <ProjectViewerModal
                    project={proposal}
                    semesterMap={semesterMap}
                  />
                  <WebsiteViewerModal project={proposal} />
                  <a
                    href={`${config.url.API_GET_PROPOSAL_PDF}?project_id=${proposal.project_id}`}
                    className="ui icon button"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="download" />
                  </a>
                  <ProjectArchivePanel
                    viewOnly={props.viewOnlyArchive}
                    project={proposal}
                    isCandidateProject={props.isCandidateProject}
                    canEditProject={
                      props.userProjects
                        ? projectPermissions[proposal.project_id]
                        : undefined
                    }
                  />
                </>
              ) : (
                <>
                  <ProjectViewerModal
                    project={proposal}
                    semesterMap={semesterMap}
                  />
                  <WebsiteViewerModal project={proposal} />
                  <a
                    href={`${config.url.API_GET_PROPOSAL_PDF}?project_id=${proposal.project_id}`}
                    className="ui icon button"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon name="download" />
                  </a>
                  {(!props.requireMembershipCheck ||
                    projectPermissions[proposal.project_id] ||
                    userContext.user?.role === USERTYPES.ADMIN) && (
                    <ProjectEditorModal
                      viewOnly={props.viewOnly}
                      project={proposal}
                      semesterData={props.semesterData}
                      activeCoaches={props.activeCoaches}
                      activeSponsors={props.activeSponsors}
                      callback={props.callback}
                    />
                  )}
                </>
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    });
  };

  const semesterName = () => {
    if (props.semester === null) {
      return "No Semester";
    }

    return props.semester?.name && props.semester.name;
  };

  const table = () => {
    return (
      <Table sortable>
        <TableHeader>
          <TableRow>
            <TableHeaderCell
            /*sorted={
                proposalData.column === COLUMNS.SEMESTER
                  ? proposalData.direction
                  : null
              }
              //onClick={() => changeSort(COLUMNS.SEMESTER)}*/
            >
              Semester
            </TableHeaderCell>
            <TableHeaderCell
            /*sorted={
                proposalData.column === COLUMNS.TITLE
                  ? proposalData.direction
                  : null
              }
              //onClick={() => changeSort(COLUMNS.TITLE)}*/
            >
              Name
            </TableHeaderCell>
            <TableHeaderCell
            /*sorted={
                proposalData.column === COLUMNS.STATUS
                  ? proposalData.direction
                  : null
              }
              //onClick={() => changeSort(COLUMNS.STATUS)}*/
            >
              Status
            </TableHeaderCell>
            <TableHeaderCell>
              {props.viewOnly ? "Current/Original" : "Edit/Original"}
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>{renderProposals()}</TableBody>
      </Table>
    );
  };

  return props.noAccordion ? (
    table()
  ) : (
    <Accordion
      fluid
      styled
      onTitleClick={() => {
        setActive(!active);
      }}
      panels={[
        {
          key: 0,
          title: semesterName(),
          active: active,
          content: { content: table() },
        },
      ]}
    />
  );
}
